import { useEffect, useState } from 'react';
import { AlertTriangle, ShieldAlert, Info, CheckCircle2, RefreshCw, FileText, MessageSquare, Lock, ArrowLeftRight, Wallet } from '../../lib/icons';
import { toast } from 'sonner';
import { synchroniserVigie, fetchAlertes, traiterAlerte, genererBrief } from '../../lib/vigie';

const ICONES = { critique: ShieldAlert, avertissement: AlertTriangle, info: Info, brief: Info };
const COULEURS = {
  critique: 'border-l-red-600 bg-red-50',
  avertissement: 'border-l-amber-500 bg-amber-50',
  info: 'border-l-slate-900 bg-slate-50',
};

const LIBELLES_ACTION = {
  voir_bon: { label: 'Voir le bon de pesée', icon: FileText },
  sms_client: { label: 'SMS d’explication au client', icon: MessageSquare },
  verrouiller_cuve: { label: 'Verrouiller la cuve', icon: Lock },
  ordre_transfert: { label: 'Ordre de transfert', icon: ArrowLeftRight },
  encaisser: { label: 'Encaisser les emballages', icon: Wallet },
};

export const FluxVigie = ({ onAction }) => {
  const [alertes, setAlertes] = useState([]);
  const [filtre, setFiltre] = useState('actives');
  const [chargement, setChargement] = useState(true);

  const charger = async () => {
    try {
      await synchroniserVigie();
      setAlertes(await fetchAlertes());
    } catch {
      toast.error('Vigie indisponible — vérifiez la connexion au serveur.');
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
    const t = setInterval(charger, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = alertes.filter((a) =>
    filtre === 'actives'
      ? !a.traite && !a.resolue
      : filtre === 'toutes'
      ? true
      : a.gravite === filtre && !a.resolue
  );
  const critiques = alertes.filter((a) => a.gravite === 'critique' && !a.traite && !a.resolue).length;

  return (
    <section data-testid="flux-vigie" className="flex h-full flex-col border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-slate-900 px-4 py-3">
        <div>
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">
            Flux de la Vigie
          </p>
          <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
            Surveillance temps réel · {critiques} alerte(s) critique(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="vigie-brief"
            onClick={async () => {
              toast.info('Génération du résumé exécutif…');
              await genererBrief();
              await charger();
              toast.success('Résumé exécutif publié dans le flux');
            }}
            className="h-9 border border-slate-300 px-3 font-heading text-xs font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            Brief du jour
          </button>
          <button
            type="button"
            data-testid="vigie-refresh"
            onClick={charger}
            className="flex h-9 items-center gap-1.5 border border-slate-300 px-3 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <RefreshCw size={13} /> Actualiser
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-slate-200">
        {[
          ['actives', 'Actives'],
          ['critique', 'Critiques'],
          ['avertissement', 'Avertissements'],
          ['toutes', 'Historique'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            data-testid={`vigie-filtre-${id}`}
            onClick={() => setFiltre(id)}
            className={`px-3 py-2 text-xs font-bold transition-colors duration-150 ${
              filtre === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {chargement && <p className="text-sm text-slate-500">Analyse des flux de l'usine…</p>}
        {!chargement && visibles.length === 0 && (
          <p data-testid="vigie-vide" className="border-2 border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
            Aucune anomalie détectée. La Vigie surveille les rendements, la saturation des cuves et
            les créances ouvertes.
          </p>
        )}
        {visibles.map((a) => {
          const Icon = ICONES[a.gravite] ?? Info;
          return (
            <article
              key={a.id}
              data-testid={`alerte-${a.id}`}
              className={`border border-slate-200 border-l-4 px-3 py-3 ${COULEURS[a.gravite] ?? COULEURS.info} ${a.traite || a.resolue ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-2">
                <Icon size={16} className="mt-0.5 flex-shrink-0 text-slate-700" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-bold text-slate-900">
                    {a.titre}
                    {a.resolue && (
                      <span className="ml-2 border border-emerald-600 px-1 font-data text-[10px] uppercase text-emerald-700">
                        résolue
                      </span>
                    )}
                  </p>
                  <p className="mt-1 whitespace-pre-line font-data text-xs leading-relaxed text-slate-700">
                    {a.message}
                  </p>
                  {a.chiffres && (
                    <dl className="mt-2 grid grid-cols-2 gap-px bg-slate-200 sm:grid-cols-3">
                      {[
                        ['Tonnage broyé', `${a.chiffres.tonnage_broye_kg} kg`],
                        ['Huile produite', `${a.chiffres.huile_produite_kg} kg`],
                        ['Rendement moyen', `${a.chiffres.rendement_moyen_pct} %`],
                        ['Trésorerie', `${a.chiffres.tresorerie_comptes_tnd} TND`],
                        ['Créances emballages', `${a.chiffres.creances_emballages_tnd} TND`],
                        ['Remplissage cuves', `${a.chiffres.remplissage_global_pct} %`],
                      ].map(([k, v]) => (
                        <div key={k} className="bg-white px-2 py-1.5">
                          <dt className="text-[10px] uppercase tracking-[0.08em] text-slate-500">{k}</dt>
                          <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(a.actions ?? []).map((act) => {
                      const cfg = LIBELLES_ACTION[act];
                      if (!cfg) return null;
                      const ActIcon = cfg.icon;
                      return (
                        <button
                          key={act}
                          type="button"
                          data-testid={`action-${act}-${a.ref ?? a.id}`}
                          onClick={() => onAction(act, a)}
                          className="flex h-8 items-center gap-1.5 border border-slate-900 bg-white px-2 font-heading text-[11px] font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <ActIcon size={12} /> {cfg.label}
                        </button>
                      );
                    })}
                    {!a.traite && !a.resolue && (
                      <button
                        type="button"
                        data-testid={`traiter-${a.id}`}
                        onClick={async () => {
                          await traiterAlerte(a.id);
                          await charger();
                        }}
                        className="flex h-8 items-center gap-1.5 border border-slate-300 px-2 text-[11px] font-semibold text-slate-600 transition-colors duration-150 hover:bg-emerald-700 hover:text-white"
                      >
                        <CheckCircle2 size={12} /> Marquer traité
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
